import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button, Table } from "react-bootstrap";
import toastr from "toastr";
import { CSVLink } from "react-csv";
import { CustomLoading } from "components/CustomLoading";
import { EmptyStateTemplate } from "./Empty_state_Template";
import CustomToolTip from "components/CustomToolTip";
import base64 from "../../data/base64.json";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { CustomDateRangePicker } from "components/CustomDateRangePicker";
import { CustomDropdown } from "components/CustomDropdown";
import "../../styles/Reports.css";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const LsaDisbursementReportTable = ({ policyId, benefitYearOptions }) => {
  const [loading, setLoading] = useState(true);
  const [disbursementData, setDisbursementData] = useState([]);
  const [dataToExport, setDataToExport] = useState([]);
  const [empColTotal, setEmpColTotal] = useState("0.00");
  const [depColTotal, setDepColTotal] = useState("0.00");
  const [totalColTotal, setTotalColTotal] = useState("0.00");
  const [showInactiveEmployees, setShowInactiveEmployees] = useState(true);
  const [selectedOption, setSelectedOption] = useState(benefitYearOptions[0]);
  const [fromDate, setFromDate] = useState(new Date(selectedOption.startDate));
  const [toDate, setToDate] = useState(new Date(selectedOption.runoffClosingDate ?? selectedOption.endDate));

  const fetchDisbursementData = useCallback(() => {
    setLoading(true);
    const apiString = `/api/Reports/disbursementbybenefityear?policyId=${policyId}&accountType=LSA&benefitYearId=${selectedOption.id}&fromDate=${fromDate.toISOString()}&toDate=${toDate.toISOString()}`;
    axios.get(apiString)
      .then(res => {
        if (res.data.success) {
          getTotals(res.data);
        } else {
          toastr.error(res.data.message, "Error");
          setLoading(false);
        }
      })
      .catch(error => {
        toastr.error(error, "Error");
        setLoading(false);
      });
  }, [policyId, selectedOption, fromDate, toDate]);

  useEffect(() => {
    fetchDisbursementData();
  }, [fetchDisbursementData, showInactiveEmployees]);

  const getTotals = (data) => {
    let empTotal = 0, depTotal = 0, total = 0;
    const exportData = data.disbursements.map(item => {
      const emp = showInactiveEmployees ? item.employeesTotal : item.activeEmployeesTotal;
      const dep = showInactiveEmployees ? item.dependentsTotal : item.activeDependentsTotal;
      const tot = showInactiveEmployees ? item.total : item.activeTotal;

      empTotal += parseFloat(emp || 0);
      depTotal += parseFloat(dep || 0);
      total += parseFloat(tot || 0);

      return [item.expenseType, emp, dep, tot];
    });

    const header = ["Expense Type", "Employees", "Dependents", "Total"];
    const totals = ["Total", empTotal.toFixed(2), depTotal.toFixed(2), total.toFixed(2)];
    const dateRange = ["Date Range", `${fromDate.toISOString().slice(0,10)} - ${toDate.toISOString().slice(0,10)}`];

    setDataToExport([dateRange, header, ...exportData, totals]);
    setDisbursementData(data.disbursements);
    setEmpColTotal(empTotal.toFixed(2));
    setDepColTotal(depTotal.toFixed(2));
    setTotalColTotal(total.toFixed(2));
    setLoading(false);
  };

  const exportToPdf = () => {
    const styles = getPdfStyles();
  
    const headerRaw = dataToExport[1];
    const bodyRaw = dataToExport.slice(2, dataToExport.length - 1);
    const footerRaw = dataToExport[dataToExport.length - 1];
  
    const headerFormatted = formatRow(headerRaw, 'tableHeader');
    const bodyFormatted = formatTableBody(bodyRaw);
    const footerFormatted = formatRow(footerRaw, 'tableFooter');
  
    const docDefinition = {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      content: [
        {
          columns: [
            createLogo(),
            createDateRange(dataToExport[0][1]),
          ],
        },
        createTitle(),
        createTable(headerFormatted, bodyFormatted, footerFormatted)
      ],
      styles: styles,
      layout: getTableLayout()
    };
  
    pdfMake.createPdf(docDefinition).download("LSA_Disbursement_Report.pdf");
  };  

  const createLogo = () => ({
    image: base64.logo,
    width: 230,
  });

  const createTitle = () => ({
      text: 'LSA Benefit Utilization Report',
      fontSize: 16,
      bold: true,
      alignment: 'center',
      margin: [10, 40, 0, 10]
  });

  const createDateRange = (dateRange) => ({
      text: `Date Range: ${dateRange}`,
      fontSize: 12,
      bold: true,
      alignment: 'right',
      margin: [0, 0, 0, 8]
  });

  const formatRow = (rowData, style) => {
      return rowData.map(cell => {
          const isNumeric = !isNaN(parseFloat(cell)) && isFinite(cell);
          const isPercentage = typeof cell === 'string' && cell.includes('%');

          return {
              text: String(cell || ''),
              style,
              alignment: (isNumeric || isPercentage) ? 'right' : 'left'
          };
      });
  };

  const formatTableBody = (rows) => rows.map(row => formatRow(row, 'tableBody'));

  const createTable = (header, body, footer) => ({
    style: 'table',
    table: {
      widths: Array(header.length).fill('*'),
      body: [header, ...body, footer]
    }
  });
  
  const getPdfStyles = () => ({
    table: {
        margin: [0, 5, 0, 15]
    },
    tableHeader: {
        bold: true,
        fontSize: 12,
        color: 'black'
    },
    tableBody: {
        fontSize: 10,
        color: 'black',
        padding: [2, 2, 2, 2]
    },
    tableFooter: {
        bold: true,
        fontSize: 10,
        color: 'black'
    }
  });

  const getTableLayout = () => ({
    hLineWidth: (i, node) => 0.5,
    vLineWidth: (i, node) => 0.5,
    paddingLeft: (i, node) => 4,
    paddingRight: (i, node) => 4
  });

  return (
    loading ? <CustomLoading /> : (
      <div className="Lsa-Disbursement-Table report-table-container">
        <div className="report-toolbar">
          <div className="toolbar-left-section">
              <div className="disbursement-report-date-tool">
                <div style={{ display: "flex", alignItems: "baseline" }}>
                  <label className="disbursement-benefit-year-dropdown-label">Benefit Year:</label>
                  <CustomDropdown
                      options={benefitYearOptions.map(opt => opt.dateRange)}
                      selectedOption={selectedOption.dateRange}
                      setSelectedOption={(opt) => {
                          const found = benefitYearOptions.find(x => x.dateRange === opt);
                          setSelectedOption(found);
                          setFromDate(new Date(found.startDate));
                          setToDate(new Date(found.runoffClosingDate ?? found.endDate));
                      }}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <CustomDateRangePicker
                    fromDate={fromDate}
                    toDate={toDate}
                    setFromDate={setFromDate}
                    setToDate={setToDate}
                    minDate={new Date(selectedOption.startDate)}
                    maxDate={new Date(selectedOption.runoffClosingDate ?? selectedOption.endDate)}
                    />
                    <CustomToolTip
                    header="Date Range Info"
                    bodyContent="The date range can only be set within the selected benefit year's period. If a runoff closing date exists, it will be used as the default end date; otherwise, the benefit year's end date will be used."
                    />
                </div>
                <label className="include-deactivated-employees">
                  <input
                      className="include-deactivated-employees-checkbox"
                      type="checkbox" 
                      checked={showInactiveEmployees} 
                      onChange={() => setShowInactiveEmployees(prev => !prev)} 
                      style={{marginLeft:"30px"}}
                  />
                  Include deactivated employees
              </label>
            </div>
          </div>
          
          <div className="toolbar-right-section">
            <div className="export-button-container">
              <Button 
                className="export-to-pdf" 
                onClick={exportToPdf}
                disabled={dataToExport.length === 0}
              >
                <span className="pdf-text">Export to PDF</span>
              </Button>
              <CSVLink 
                data={dataToExport} 
                filename="LSA_Disbursement_Report.csv"
                className="export-button"
                onClick={(event) => { if (dataToExport.length === 0) event.preventDefault(); }}
              >
                <Button 
                    variant="primary" 
                    className="csv-button export-to-csv"
                    disabled={dataToExport.length === 0}
                >
                    <span className="csv-text">Export to CSV</span>
                </Button>
              </CSVLink>
            </div>
          </div>
        </div>
        {disbursementData.length === 0 ? <EmptyStateTemplate /> : (
          <Table hover className="report-table">
            <thead>
              <tr>
                <th className="table-text-content">Expense Type</th>
                <th>
                  Employees
                  <CustomToolTip header="Employees" bodyContent="Claim amount excluding taxes and admin fees." />
                </th>
                <th>
                  Dependents
                  <CustomToolTip header="Dependents" bodyContent="Claim amount excluding taxes and admin fees." />
                </th>
                <th>
                  Total
                  <CustomToolTip header="Total" bodyContent="Claim amount excluding taxes and admin fees." />
                </th>
              </tr>
            </thead>
            <tbody>
              {disbursementData.map((item, index) => (
                <tr key={index} className="table-body-itme">
                  <td className="table-text-content">{item.expenseType}</td>
                  <td>{showInactiveEmployees ? item.employeesTotal : item.activeEmployeesTotal}</td>
                  <td>{showInactiveEmployees ? item.dependentsTotal : item.activeDependentsTotal}</td>
                  <td>{showInactiveEmployees ? item.total : item.activeTotal}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="table-footer-item total-amount">Total</td>
                <td className="table-footer-item">{empColTotal}</td>
                <td className="table-footer-item">{depColTotal}</td>
                <td className="table-footer-item">{totalColTotal}</td>
              </tr>
            </tfoot>
          </Table>
        )}
      </div>
    )
  );
};

export default LsaDisbursementReportTable;
